/**
 * Simplified Slider Component with guaranteed Auto-scroll functionality
 * For Shopify themes - specifically designed for shop-strip carousel
 */

if (!customElements.get('slider-component')) {
  customElements.define('slider-component', class SliderComponent extends HTMLElement {
    constructor() {
      super();
      
      // Core elements
      this.slider = this.querySelector('[id^="Slider-"]');
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
      this.buttons = {
        prev: this.querySelector('button[name="previous"]'),
        next: this.querySelector('button[name="next"]')
      };
      
      // Auto-scroll configuration
      this.autoScroll = this.hasAttribute('data-autoplay') || this.dataset.autoplay === 'true';
      this.autoScrollSpeed = parseInt(this.dataset.autoplaySpeed || 5) * 1000;
      this.autoScrollTimer = null;
      
      // Initialize
      this.init();
    }
    
    init() {
      if (!this.slider || this.sliderItems.length < 2) return;
      
      // Setup navigation buttons
      if (this.buttons.prev) this.buttons.prev.addEventListener('click', this.onButtonClick.bind(this));
      if (this.buttons.next) this.buttons.next.addEventListener('click', this.onButtonClick.bind(this));
      
      // Enable auto-scroll if configured
      if (this.autoScroll) {
        console.log("Auto-scroll enabled with speed:", this.autoScrollSpeed);
        this.startAutoScroll();
        
        // Pause on interaction
        this.addEventListener('mouseenter', this.pauseAutoScroll.bind(this));
        this.addEventListener('mouseleave', this.startAutoScroll.bind(this));
        this.addEventListener('touchstart', this.pauseAutoScroll.bind(this), { passive: true });
        this.addEventListener('touchend', this.startAutoScroll.bind(this), { passive: true });
      }
      
      // Basic swipe detection for mobile
      this.initTouchHandling();
    }
    
    startAutoScroll() {
      if (!this.autoScroll) return;
      this.pauseAutoScroll(); // Clear existing timer if any
      
      this.autoScrollTimer = setInterval(() => {
        this.nextSlide();
      }, this.autoScrollSpeed);
      
      console.log("Auto-scroll started");
    }
    
    pauseAutoScroll() {
      if (this.autoScrollTimer) {
        clearInterval(this.autoScrollTimer);
        this.autoScrollTimer = null;
        console.log("Auto-scroll paused");
      }
    }
    
    nextSlide() {
      const scrollAmount = this.slider.clientWidth;
      const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
      
      // If we're near the end, loop back to start
      if (this.slider.scrollLeft + scrollAmount >= maxScroll - 20) {
        // Smooth scroll back to the beginning
        this.slider.scrollTo({
          left: 0,
          behavior: 'smooth'
        });
      } else {
        // Otherwise scroll forward
        this.slider.scrollTo({
          left: this.slider.scrollLeft + scrollAmount,
          behavior: 'smooth'
        });
      }
      
      // Update counter if present
      this.updateCounter();
    }
    
    prevSlide() {
      const scrollAmount = this.slider.clientWidth;
      const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
      
      // If we're near the start, loop to the end
      if (this.slider.scrollLeft - scrollAmount <= 20) {
        this.slider.scrollTo({
          left: maxScroll,
          behavior: 'smooth'
        });
      } else {
        this.slider.scrollTo({
          left: this.slider.scrollLeft - scrollAmount,
          behavior: 'smooth'
        });
      }
      
      // Update counter if present
      this.updateCounter();
    }
    
    onButtonClick(event) {
      event.preventDefault();
      
      if (event.currentTarget.name === 'next') {
        this.nextSlide();
      } else {
        this.prevSlide();
      }
      
      // Restart auto-scroll after manual navigation
      if (this.autoScroll) {
        this.startAutoScroll();
      }
    }
    
    updateCounter() {
      const currentCounter = this.querySelector('.slider-counter--current');
      const totalCounter = this.querySelector('.slider-counter--total');
      
      if (!currentCounter) return;
      
      // Calculate current slide
      const scrollPosition = this.slider.scrollLeft;
      const slideWidth = this.slider.clientWidth;
      const currentIndex = Math.round(scrollPosition / slideWidth) + 1;
      
      // Update counter
      currentCounter.textContent = Math.min(currentIndex, this.sliderItems.length);
      
      // Update total if needed
      if (totalCounter && !totalCounter.textContent) {
        totalCounter.textContent = this.sliderItems.length;
      }
    }
    
    initTouchHandling() {
      let touchStartX = 0;
      let touchEndX = 0;
      
      this.slider.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
        this.pauseAutoScroll();
      }, { passive: true });
      
      this.slider.addEventListener('touchend', (e) => {
        touchEndX = e.changedTouches[0].screenX;
        const touchDiff = touchStartX - touchEndX;
        
        // Detect swipe (minimum 50px movement)
        if (Math.abs(touchDiff) > 50) {
          if (touchDiff > 0) {
            this.nextSlide();
          } else {
            this.prevSlide();
          }
        }
        
        // Resume auto-scroll
        if (this.autoScroll) {
          this.startAutoScroll();
        }
      }, { passive: true });
    }
    
    // Handle visibility changes (pause when not visible)
    connectedCallback() {
      // Use Intersection Observer to detect when slider is visible
      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) {
            if (this.autoScroll) this.startAutoScroll();
          } else {
            this.pauseAutoScroll();
          }
        }, { threshold: 0.1 });
        
        this.visibilityObserver.observe(this);
      }
      
      // Ensure auto-scroll doesn't run in background tabs
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.pauseAutoScroll();
        } else if (this.autoScroll) {
          this.startAutoScroll();
        }
      });
    }
    
    disconnectedCallback() {
      this.pauseAutoScroll();
      if (this.visibilityObserver) {
        this.visibilityObserver.disconnect();
      }
    }
  });
}

// Add fallback initialization for when the DOM is already loaded
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('slider-component[data-autoplay="true"]').forEach(slider => {
    // Force re-initialization if needed
    if (slider.autoScrollTimer === undefined) {
      Object.getPrototypeOf(slider).constructor.call(slider);
    }
  });
});